import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Crown, ListChecks, Wand2, BarChart3 } from 'lucide-react';

import { useProfileMeQuery, useMeQuery } from '@/services/qbotApi';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { cn } from '@/lib/cn';
import type { QbotApiError } from '@/types/qbot';

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

/** Best-effort JSON for surfacing an error body in dev diagnostics. */
function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v) ?? '';
  } catch {
    return '';
  }
}

interface NavTileProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  badge?: string;
}

function NavTile({ to, icon, title, subtitle, badge }: NavTileProps) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-bg-tertiary text-brand">
        {icon}
      </span>
      <div className="flex flex-1 flex-col">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-fg">{title}</span>
          {badge && (
            <span className="rounded-full bg-brand/15 px-2 text-[10px] font-bold uppercase text-brand">
              {badge}
            </span>
          )}
        </div>
        <span className="text-[11px] text-fg-muted">{subtitle}</span>
      </div>
      <ChevronRight aria-hidden className="h-5 w-5 text-fg-muted" />
    </Link>
  );
}

/**
 * CR "Mazzi" landing — player snapshot (name / tag / trophies / arena +
 * current deck) followed by the deck nav tiles: saved, popular (meta), deck
 * check, and the AI deck builder (which previously had no entry point).
 *
 * Battles live on their own Hub button (/cr/battles), so this screen is
 * deck-focused only. If the user hits the URL without a linked CR tag
 * (NOT_LINKED), we redirect to /cr/link rather than render a broken state —
 * same logic the old CRHome used.
 */
export function CRDecks() {
  const { t } = useTranslation('cr');
  // /me decides linked status; /profile/me actually pulls CR data.
  const { data: identity } = useMeQuery();
  const { data: profile, isLoading, error } = useProfileMeQuery();

  // Resolve the error shape once (used for both not-linked routing + error UI).
  const errCode = isQbotApiError(error) ? error.data?.error?.code : undefined;
  const errStatus = isQbotApiError(error) ? error.status : undefined;

  // No linked CR tag (either /me says so, or /profile/me reports not-linked /
  // 404) → bounce to the link flow rather than render a broken state.
  const notLinked =
    (identity != null && identity.linkedCrTag == null) ||
    errCode === 'NOT_LINKED' ||
    errCode === 'NOT_FOUND' ||
    errStatus === 404;
  if (notLinked) {
    return <Navigate to="/cr/link" replace />;
  }

  if (isLoading) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('home.loading')}</div>;
  }

  // A real error (auth / upstream / network) must NOT spin forever as "loading".
  if (error || !profile) {
    const detail = import.meta.env.DEV
      ? ` [${String(errStatus ?? '?')} ${isQbotApiError(error) ? safeStringify(error.data) : ''}]`
      : '';
    return (
      <div className="flex flex-col items-start gap-3 px-4 py-6">
        <p className="text-sm text-danger">
          {t('home.error', {
            defaultValue: "Couldn't load your Clash Royale profile.",
          })}
          {detail}
        </p>
        <Link
          to="/cr/link"
          className="rounded-lg bg-bg-tertiary px-3 py-2 text-xs font-bold text-fg-muted transition-opacity active:opacity-80"
        >
          {t('home.changeTag')}
        </Link>
      </div>
    );
  }

  const { player } = profile;

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto px-4 py-4">
      {/* Player header */}
      <header className="flex items-center gap-3 rounded-xl bg-bg-secondary p-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand/15 text-brand">
          <Crown className="h-7 w-7" strokeWidth={2.5} />
        </div>
        <div className="flex flex-1 flex-col min-w-0">
          <h1 className="truncate text-lg font-bold text-fg">{player.name}</h1>
          <span className="text-[11px] text-fg-muted tabular-nums">{player.tag}</span>
          <div className="mt-1 flex items-center gap-3 text-[11px] text-fg-muted">
            <span>
              <span className="font-bold text-fg">{player.trophies.toLocaleString()}</span> 🏆
            </span>
            <span>{player.arena?.name ?? '—'}</span>
          </div>
        </div>
      </header>

      {/* Current deck */}
      {player.currentDeck?.length === 8 && (
        <section className="card-edge rounded-xl bg-bg-secondary p-3">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-fg-muted">
              {t('home.currentDeck')}
            </h2>
            <Link
              to="/cr/decks/check"
              className="text-[11px] font-semibold text-brand transition-opacity active:opacity-70"
            >
              {t('home.checkDeck')} →
            </Link>
          </div>
          <DeckGrid cards={player.currentDeck} avgElixir={undefined} />
        </section>
      )}

      {/* Deck nav tiles */}
      <nav className="flex flex-col gap-2">
        <NavTile
          to="/cr/decks/check"
          icon={<BarChart3 className="h-5 w-5" strokeWidth={2.5} />}
          title={t('decks.tiles.check.title', { defaultValue: 'Deck check' })}
          subtitle={t('decks.tiles.check.subtitle', {
            defaultValue: 'Rate your current deck + AI analysis',
          })}
        />
        <NavTile
          to="/cr/decks/popular"
          icon={<Crown className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.tiles.popular.title')}
          subtitle={t('home.tiles.popular.subtitle')}
        />
        <NavTile
          to="/cr/decks/saved"
          icon={<ListChecks className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.tiles.saved.title')}
          subtitle={t('home.tiles.saved.subtitle')}
        />
        <NavTile
          to="/cr/decks/builder"
          icon={<Wand2 className="h-5 w-5" strokeWidth={2.5} />}
          title={t('decks.tiles.builder.title', { defaultValue: 'AI deck builder' })}
          subtitle={t('decks.tiles.builder.subtitle', {
            defaultValue: 'Describe a style, get a deck',
          })}
          badge="PRO"
        />
      </nav>

      <Link
        to="/cr/link"
        className={cn(
          'mt-auto rounded-lg border border-border bg-bg-secondary py-2 text-center text-xs text-fg-muted',
          'transition-opacity active:opacity-70',
        )}
      >
        {t('home.changeTag')}
      </Link>
    </div>
  );
}
